import React, { useState, useEffect } from 'react';
import { api, API_ROUTERS } from '../../../services/api';
import CadastrosOcorrenciaView from './view';
import {
  FormDataOcorrencia,
  Ocorrencia,
  Subocorrencia,
} from './types';
import Store, { useAppSelector, useAppDispatch } from '../../../store';

const CadastrosOcorrencia: React.FC = () => {
  const initialFormData: FormDataOcorrencia = {
    codigo: undefined,
    descricao: '',
    subocorrencia: false,
    fotocanhoto: false,
    justificativa: false,
    empresa: '',
    subocorrencias: [],
  };

  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<FormDataOcorrencia>(initialFormData);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [ocorrencias, setOcorrencias] = useState<Ocorrencia[]>([]);
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (user.empresa && user.empresa !== formData.empresa) {
      setFormData((prevData) => ({
        ...prevData,
        empresa: user.empresa,
      }));
    }
  }, [formData.empresa, user.empresa]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // Atualiza o formData com o novo valor
    const newValue =
      typeof value === 'string' && name === 'subocorrencia'
        ? value === 'true'
        : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (name === 'subocorrencia') {
      const isSubocorrencia =
        typeof value === 'string' ? value === 'true' : Boolean(value);
      if (!isSubocorrencia) {
        setFormData((prev) => ({
          ...prev,
          subocorrencias: [],
        }));
      }
    }
  };

  // Método específico para lidar com checkboxes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));

    // Se desmarcar subocorrencia, limpa a lista de subocorrências
    if (name === 'subocorrencia' && !checked) {
      setFormData((prev) => ({
        ...prev,
        subocorrencias: [],
      }));
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = async () => {
    setError('');
    setSuccess('');
    setOcorrencias([]);

    try {
      Store.load.show(dispatch, 'Consultando ocorrências');
      const response = await api.get(API_ROUTERS.OCORRENCIA);
      let allOcorrencias: Ocorrencia[] = api.utils.trataResponseArray(
        response
      ) as Ocorrencia[];

      if (!Array.isArray(allOcorrencias) || allOcorrencias.length === 0) {
        setError('Nenhuma ocorrência encontrada');
        setOcorrencias([]);
        Store.load.hide(dispatch);
        return;
      }

      if (searchTerm.trim() === '') {
        setOcorrencias(allOcorrencias);
        setSuccess('Selecione uma ocorrência da lista');
        Store.load.hide(dispatch);
        return;
      }

      const searchCode = Number(searchTerm);

      if (!isNaN(searchCode)) {
        const ocorrenciaByCode = allOcorrencias.find(
          (ocorrencia) => ocorrencia.codigo === searchCode
        );

        if (ocorrenciaByCode) {
          setOcorrencias([ocorrenciaByCode]);
          setSuccess('Ocorrência encontrada');
        } else {
          setError('Nenhuma ocorrência encontrada com este código');
        }
      } else {
        const searchTermLower = searchTerm.toLowerCase();
        const filteredOcorrencias = allOcorrencias.filter((ocorrencia: any) => {
          const descricao =
            typeof ocorrencia.descricao === 'string'
              ? ocorrencia.descricao.toLowerCase()
              : '';
          return descricao.includes(searchTermLower);
        });

        setOcorrencias(filteredOcorrencias);

        if (filteredOcorrencias.length === 0) {
          setError('Nenhuma ocorrência encontrada com o termo pesquisado');
        } else {
          setSuccess(`${filteredOcorrencias.length} ocorrência(s) encontrada(s)`);
        }
      }
    } catch (err) {
      console.error('Erro na pesquisa:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao buscar ocorrências');
      }
      setOcorrencias([]);
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const handleEdit = (ocorrencia: Ocorrencia) => {
    setFormData({
      codigo: ocorrencia.codigo,
      descricao: ocorrencia.descricao,
      subocorrencia: ocorrencia.subocorrencia,
      fotocanhoto: ocorrencia.fotocanhoto,
      justificativa: ocorrencia.justificativa,
      empresa: ocorrencia.empresa || user.empresa,
      subocorrencias: ocorrencia.subocorrencias || [],
    });

    setEditingId(ocorrencia.codigo);

    // Atualiza o campo de pesquisa com o código da ocorrência
    setSearchTerm(ocorrencia.codigo.toString());
    setSuccess('Ocorrência carregada para edição');
    setOcorrencias([]);
  };

  const handleDelete = async (codigo: number) => {
    if (!codigo) {
      setError('Código de ocorrência inválido');
      return;
    }

    try {
      Store.load.show(dispatch, 'Excluindo ocorrência');

      await api.delete(`${API_ROUTERS.OCORRENCIA}/${codigo}`);

      // Usando dialog para mensagem de sucesso
      Store.dialog.alert(dispatch, 'Ocorrência excluída com sucesso!', 'success');

      // Se tiver ocorrências na lista, atualiza removendo a excluída
      if (ocorrencias.length > 0) {
        setOcorrencias(ocorrencias.filter(o => o.codigo !== codigo));
      }

      // Se estiver editando essa ocorrência, limpa o formulário
      if (editingId === codigo) {
        setFormData(initialFormData);
        setEditingId(null);
      }
    } catch (err) {
      console.error('Erro ao excluir:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao excluir ocorrência');
      }
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const handleAddSubocorrencia = () => {
    const newSubocorrencia: Subocorrencia = {
      codigo: 0, // Será definido pelo backend
      codigoocorrencia: editingId || 0,
      descricao: '',
      justificativa: false,
    };

    setFormData((prev) => ({
      ...prev,
      subocorrencias: [...prev.subocorrencias, newSubocorrencia],
    }));
  };

  const handleRemoveSubocorrencia = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      subocorrencias: prev.subocorrencias.filter((_, i) => i !== index),
    }));
  };

  const handleSubocorrenciaChange = (
    index: number,
    field: keyof Subocorrencia,
    value: string | boolean
  ) => {
    setFormData((prev) => {
      const updatedSubocorrencias = [...prev.subocorrencias];
      updatedSubocorrencias[index] = {
        ...updatedSubocorrencias[index],
        [field]: value,
      };
      return {
        ...prev,
        subocorrencias: updatedSubocorrencias,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!formData.descricao) {
        throw new Error('Por favor, preencha a descrição da ocorrência');
      }

      if (!formData.empresa) {
        throw new Error(
          'Empresa não identificada. Por favor, faça login novamente.'
        );
      }

      // Verificar se todas as subocorrências têm descrição
      if (
        formData.subocorrencia &&
        formData.subocorrencias.some((s) => !s.descricao.trim())
      ) {
        throw new Error('Todas as subocorrências devem ter uma descrição');
      }

      Store.load.show(dispatch, 'Salvando ocorrência');

      // Atualiza o editingId com base no código do formData
      if (formData.codigo && !editingId) {
        setEditingId(formData.codigo);
      }

      const dataToSend = {
        codigo: formData.codigo,
        descricao: formData.descricao,
        subocorrencia: formData.subocorrencia,
        fotocanhoto: formData.fotocanhoto,
        justificativa: formData.justificativa,
        empresa: formData.empresa,
        subocorrencias: formData.subocorrencias.map((sub) => ({
          codigo: sub.codigo,
          descricao: sub.descricao,
          justificativa: sub.justificativa,
        })),
      };

      // Se tem um ID, está atualizando; se não, está criando
      const isUpdate = Boolean(formData.codigo);

      let response;
      if (isUpdate) {
        response = await api.put(`${API_ROUTERS.OCORRENCIA}/${formData.codigo}`, dataToSend);
      } else {
        response = await api.post(API_ROUTERS.OCORRENCIA, dataToSend);
      }

      if (ocorrencias.length > 0 && response.data && response.data.codigo) {
        const novoCodigo = response.data.codigo;

        if (isUpdate) {
          // Atualiza a lista se estiver editando
          setOcorrencias(prevOcorrencias =>
            prevOcorrencias.map(o =>
              o.codigo === formData.codigo ? { ...dataToSend, codigo: formData.codigo } as Ocorrencia : o
            )
          );
        } else {
          // Adiciona à lista se estiver criando
          setOcorrencias(prevOcorrencias => [
            ...prevOcorrencias,
            {
              ...dataToSend,
              codigo: novoCodigo,
            } as Ocorrencia,
          ]);
        }
      }

      Store.dialog.alert(dispatch, `Ocorrência ${isUpdate ? 'atualizada' : 'cadastrada'} com sucesso!`, 'success');
      setFormData(initialFormData);
      setEditingId(null);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Ocorreu um erro ao processar a solicitação');
      }
    } finally {
      Store.load.hide(dispatch);
    }
  };

  return (
    <CadastrosOcorrenciaView
      formData={formData}
      error={error}
      success={success}
      loading={loading}
      searchTerm={searchTerm}
      ocorrencias={ocorrencias}
      handleChange={handleChange}
      handleCheckboxChange={handleCheckboxChange}
      handleSubmit={handleSubmit}
      handleSearch={handleSearch}
      handleSearchInputChange={handleSearchInputChange}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      setOcorrencias={setOcorrencias}
      setSearchTerm={setSearchTerm}
      setSuccess={setSuccess}
      setFormData={setFormData}
      handleAddSubocorrencia={handleAddSubocorrencia}
      handleRemoveSubocorrencia={handleRemoveSubocorrencia}
      handleSubocorrenciaChange={handleSubocorrenciaChange}
    />
  );
};

export default CadastrosOcorrencia;
